import React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import Chip from 'core-system/Chip'
import Loading from 'core-system/Loading'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import { AppState } from 'redux/config/store'
import { ReimbursementStatus } from 'redux/reimbursements/reimbursementsTypes'
import { CurrencyType } from 'features/Programs/Shared/types'
import { formatCurrency } from 'shared/CurrencyFunctions'
import { Alert } from 'core-system/Icons/Misc'
import ReceiptLink from './ReceiptLink'

const statusMap: Record<ReimbursementStatus, string> = {
  CREATED: 'yellow',
  PROCESSED: 'blue',
  SUBMITTED: 'purple',
  APPROVED: 'green',
  REJECTED: 'red',
  PARTIAL: 'blue',
}

const statusDisplayMap: Record<ReimbursementStatus, string> = {
  CREATED: 'Under Review',
  PROCESSED: 'Processed',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PARTIAL: 'Partial',
}

const EmptyContainer = styled.div`
  padding: ${(props) => props.theme.pxToRem(40)};
  height: ${(props) => props.theme.pxToRem(200)};
  background-color: ${(props) => props.theme.palette.white};
  color: ${(props) => props.theme.palette.text.primary};
  border-radius: 0.9375rem;
  box-shadow: ${(props) => props.theme.dropShadows.normal};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const EmptyText = styled.div`
  ${(props) => props.theme.typography.action3}
`

const HeaderCell = (text: string) => (
  <TableRowCell>
    <Text fontWeight={600} variant='action3'>
      {text}
    </Text>
  </TableRowCell>
)

const formatExpenseType = (expenseType: string | null) => {
  if (!expenseType) return '-'
  return expenseType.charAt(0) + expenseType.slice(1).toLowerCase()
}

export interface ReimbursementRowData {
  id: string
  receiptDate: string
  submittedAt: string
  amount: number
  approvedAmount?: number
  status: ReimbursementStatus
  expenseType: string | null
  employeeName?: string
  note?: string | null
  receiptUrl?: string | null
  merchantCurrency?: string | null
  manualReview?: boolean
}

interface ReimbursementsTableProps {
  data: ReimbursementRowData[] | null
  loading?: boolean
  variant: 'employee' | 'admin'
  emptyMessage?: string
  emptySubtitle?: string
  onRowClick?: (id: string) => void
}

const ReimbursementsTable = React.memo(
  ({
    data,
    loading = false,
    variant,
    emptyMessage = 'No Reimbursements Found',
    emptySubtitle,
    onRowClick,
  }: ReimbursementsTableProps) => {
    const conversionRates = useSelector(
      (state: AppState) => state.application.properties
    )

    if (loading || data === null) {
      return <Loading marginTop='0.75rem' />
    }

    if (data.length === 0) {
      return (
        <EmptyContainer>
          <EmptyText>{emptyMessage}</EmptyText>
          {emptySubtitle && (
            <Text
              variant='body2'
              textColor={palette.text.secondary}
              marginTop='0.5rem'
            >
              {emptySubtitle}
            </Text>
          )}
        </EmptyContainer>
      )
    }

    const employeeHeaderCells = [
      HeaderCell('Submitted On'),
      HeaderCell('Receipt Date'),
      HeaderCell('Type'),
      HeaderCell('Amount'),
      HeaderCell('Approved Amount'),
      HeaderCell('Status'),
      HeaderCell('Receipt'),
      HeaderCell('Note'),
    ]

    const adminHeaderCells = [
      HeaderCell('Submitted On'),
      HeaderCell('Receipt Date'),
      HeaderCell('Employee'),
      HeaderCell('Expense Type'),
      HeaderCell('Amount'),
      HeaderCell('Approved Amount'),
      HeaderCell('Status'),
      HeaderCell('Receipt'),
      HeaderCell('Note'),
      HeaderCell('Manual Review'),
    ]

    const headerCells =
      variant === 'employee' ? employeeHeaderCells : adminHeaderCells

    const sortedData = [...data].sort(
      (a, b) =>
        moment(b.submittedAt).valueOf() - moment(a.submittedAt).valueOf()
    )

    const rows = sortedData.map((row) => (
      <TableRow
        key={row.id}
        onClick={onRowClick ? () => onRowClick(row.id) : undefined}
        style={onRowClick ? { cursor: 'pointer' } : undefined}
      >
        <TableRowCell>
          {moment(row.submittedAt).format('MMM D, YYYY')}
        </TableRowCell>
        <TableRowCell>
          {moment(row.receiptDate).format('MMM D, YYYY')}
        </TableRowCell>
        {variant === 'employee' ? (
          <>
            <TableRowCell>{formatExpenseType(row.expenseType)}</TableRowCell>
            <TableRowCell>
              {formatCurrency(
                (row.merchantCurrency as CurrencyType) || 'USD',
                row.amount,
                conversionRates
              )}
            </TableRowCell>
            <TableRowCell>
              {row.approvedAmount != null
                ? formatCurrency(
                    (row.merchantCurrency as CurrencyType) || 'USD',
                    row.approvedAmount,
                    conversionRates
                  )
                : '-'}
            </TableRowCell>
            <TableRowCell>
              <Chip variant={statusMap[row.status] || 'grey'}>
                {statusDisplayMap[row.status]}
              </Chip>
            </TableRowCell>
            <TableRowCell>
              <ReceiptLink receiptUrl={row.receiptUrl} stopPropagation />
            </TableRowCell>
            <TableRowCell>{row.note || '-'}</TableRowCell>
          </>
        ) : (
          <>
            <TableRowCell>{row.employeeName || '-'}</TableRowCell>
            <TableRowCell>{formatExpenseType(row.expenseType)}</TableRowCell>
            <TableRowCell>
              {formatCurrency(
                (row.merchantCurrency as CurrencyType) || 'USD',
                row.amount,
                conversionRates
              )}
            </TableRowCell>
            <TableRowCell>
              {row.approvedAmount != null
                ? formatCurrency(
                    (row.merchantCurrency as CurrencyType) || 'USD',
                    row.approvedAmount,
                    conversionRates
                  )
                : '-'}
            </TableRowCell>
            <TableRowCell>
              <Chip variant={statusMap[row.status] || 'grey'}>
                {statusDisplayMap[row.status]}
              </Chip>
            </TableRowCell>
            <TableRowCell>
              <ReceiptLink receiptUrl={row.receiptUrl} stopPropagation />
            </TableRowCell>
            <TableRowCell>{row.note || '-'}</TableRowCell>
            <TableRowCell>
              {row.manualReview ? (
                <Alert
                  width={18}
                  height={18}
                  color={palette.warning.yellow700}
                />
              ) : (
                '-'
              )}
            </TableRowCell>
          </>
        )}
      </TableRow>
    ))

    return <Table headerCells={headerCells} rows={rows} maxRows={10} />
  }
)

if (process.env.NODE_ENV !== 'production') {
  ReimbursementsTable.displayName = 'ReimbursementsTable'
}

export default ReimbursementsTable
